import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { incrementCumulativeTime, getProgress, CourseProgress, updateLessonProgress } from '@features/courses/api/progressServices';
import { useAuth } from '@features/auth';
import { appConfig } from '@/shared/constants';

import useSessionData from '@/shared/hooks/useSessionData';
import useActivityTracking from '@/features/dashboard/hooks/useActivityTracking';
import { useComplianceSessionManager } from '@/features/courses/hooks/useComplianceSessionManager';
import { useComplianceBreakManager, BreakHistoryItem } from '@/features/courses/hooks/useComplianceBreakManager';

// Import New Standalone Modular Hooks
import { useComplianceHydration } from '@/features/courses/hooks/useComplianceHydration';
import { useMasterClock } from '@/features/courses/hooks/useMasterClock';
import { useSessionUnloadBeacon } from '@/features/courses/hooks/useSessionUnloadBeacon';
import { useTimerSimulator } from '@/features/courses/hooks/useTimerSimulator';

const MAX_DAILY_HOURS = 4 * 3600;
const BREAK_REQUIRED_AFTER = 2 * 3600;

interface VideoProgress {
  currentTime: number;
  duration: number;
  percentWatched: number;
}

interface TimerContextValue {
  sessionTime: number;
  totalTime: number;
  totalCourseTime: number;
  isActive: boolean;
  isPaused: boolean;
  breakTime: number;
  isOnBreak: boolean;
  isLockedOut: boolean;
  isBreakMandatory: boolean;
  currentSessionId: string | null;
  videoProgress: VideoProgress | null;
  lessonsAccessed: string[];
  sessionHistory: any[];
  breakHistory: BreakHistoryItem[];
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  startTimer: () => Promise<void>;
  pauseTimer: () => Promise<void>;
  resumeTimer: () => Promise<void>;
  stopTimer: () => Promise<void>;
  resetSessionTimer: () => void;
  startBreak: () => Promise<void>;
  endBreak: () => Promise<void>;
  triggerPVQ: () => Promise<void>;
  updateVideoProgress: (currentTime: number, duration: number, percentWatched: number) => void;
  trackLessonAccess: (newLessonId: string) => void;
  isBreakRecommended: () => boolean;
  isMaxDailyHoursReached: () => boolean;
  getFormattedSessionTime: () => string;
  getFormattedTotalTime: () => string;
  getFormattedBreakTime: () => string;
  getRemainingDailyTime: () => number;
  formatTime: (seconds: number) => string;
  getActualBreakDuration: () => number;
  hasBreakMetMinimumDuration: () => boolean;
  lessonTime: number;
  resetLessonTime: () => void;
  sessionTimer: any;
  timerRefs: any;
  checkDailyLockout: (checkFunction: () => Promise<boolean>) => Promise<boolean | undefined>;
  timeUntilBreak: number;
  isInitialized: boolean;
  activityTracking?: any;
}

export const TimerContext = createContext<TimerContextValue | undefined>(undefined);

export const useTimer = (): TimerContextValue => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

interface TimerProviderProps {
  children: ReactNode;
  courseId?: string;
  lessonId?: string | null;
  ipAddress?: string;
}

export const TimerProvider: React.FC<TimerProviderProps> = ({ children, courseId = '', lessonId, ipAddress = '' }) => {
  const { user } = useAuth();

  // State managed by Domain Managers
  const [videoProgress, setVideoProgress] = useState<VideoProgress | null>(null);
  const [lessonTime, setLessonTime] = useState<number>(0);
  const [currentLessonId, setCurrentLessonId] = useState<string | null>(lessonId || null);

  // Unified Master Clock State
  const [sessionTime, setSessionTime] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [totalCourseTime, setTotalCourseTime] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isLockedOut, setIsLockedOut] = useState<boolean>(false);
  const [breakRequired, setBreakRequired] = useState<boolean>(false);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(
    import.meta.env.VITE_TEST_MODE === 'true' || import.meta.env.MODE === 'test'
  );
  const [error, setError] = useState<string | null>(null);

  // Data persistence for credit lookups
  const progressDataRef = useRef<CourseProgress | null>(null);

  // High-frequency loops and ticker references
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const saveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const beforeUnloadHandlerRef = useRef<((event: BeforeUnloadEvent) => void) | null>(null);
  const lastSyncedTimeRef = useRef<number>(0);
  const lastTickRef = useRef<number | null>(null);
  const breakCheckRef = useRef<boolean>(false);
  const lastBreakTimeRef = useRef<number>(0);

  // High-frequency clock refs to prevent React re-render overhead
  const sessionTimeRef = useRef<number>(0);
  const totalTimeRef = useRef<number>(0);
  const totalCourseTimeRef = useRef<number>(0);
  const lessonTimeRef = useRef<number>(0);
  const currentLessonIdRef = useRef<string | null>(lessonId || null);
  const videoProgressRef = useRef<VideoProgress | null>(null);
  const breakHistoryRef = useRef<BreakHistoryItem[]>([]);

  const timerRefs = useMemo(() => ({
    sessionTime: sessionTimeRef,
    totalTime: totalTimeRef,
    totalCourseTime: totalCourseTimeRef,
    lessonTime: lessonTimeRef,
    videoProgress: videoProgressRef,
    breakHistory: breakHistoryRef
  }), []);

  const sessionData = useSessionData({
    onSessionCreated: undefined,
    onLessonAccessed: undefined,
    onSessionClosed: undefined
  });
  
  const activityTracking = useActivityTracking(isActive);

  const SESSION_TIME_KEY = sessionData.currentSessionId ? `fastrack_session_time_${sessionData.currentSessionId}` : null;

  // Placeholder refs for compliance-wrapped functions to break circular dependencies
  const startTimerRef = useRef<() => Promise<void>>(async () => { console.warn('[TimerContext] startTimer called before initialization'); });
  const stopTimerRef = useRef<() => Promise<void>>(async () => { console.warn('[TimerContext] stopTimer called before initialization'); });
  const pauseTimerRef = useRef<() => Promise<void>>(async () => { console.warn('[TimerContext] pauseTimer called before initialization'); });
  const resumeTimerRef = useRef<() => Promise<void>>(async () => { console.warn('[TimerContext] resumeTimer called before initialization'); });

  const startTimerComplianceWrapped = useCallback(async () => startTimerRef.current(), []);
  const stopTimerComplianceWrapped = useCallback(async () => stopTimerRef.current(), []);
  const pauseTimerComplianceWrapped = useCallback(async () => pauseTimerRef.current(), []);
  const resumeTimerComplianceWrapped = useCallback(async () => resumeTimerRef.current(), []);

  // Raw Timer Implementations (Internal Master Clock Controls)
  const startTimerImpl = useCallback(async () => {
    if (!isActive && !isLockedOut) {
      setIsActive(true);
      setIsPaused(false);
      lastTickRef.current = Date.now();
    }
  }, [isActive, isLockedOut]);

  const pauseTimerImpl = useCallback(async () => {
    if (isActive && !isPaused) {
      setIsPaused(true);
      lastTickRef.current = null;
    }
  }, [isActive, isPaused]);

  const resumeTimerImpl = useCallback(async () => {
    if (isActive && isPaused) {
      setIsPaused(false);
      lastTickRef.current = Date.now();
    }
  }, [isActive, isPaused]);

  const stopTimerImpl = useCallback(async () => {
    setIsActive(false);
    setIsPaused(false);
    setSessionTime(0);
    sessionTimeRef.current = 0;
    lastTickRef.current = null;
    breakCheckRef.current = false;
    if (SESSION_TIME_KEY) {
      try {
        localStorage.removeItem(SESSION_TIME_KEY);
      } catch (error) {
        console.error('[TimerContext] Failed to clear session time:', error);
      }
    }
  }, [SESSION_TIME_KEY]);

  const loadDailyTime = useCallback((dailyTimeInSeconds: number): void => {
    if (typeof dailyTimeInSeconds === 'number' && dailyTimeInSeconds >= 0) {
      setTotalTime(dailyTimeInSeconds);
      totalTimeRef.current = dailyTimeInSeconds;
      if (dailyTimeInSeconds >= MAX_DAILY_HOURS) {
        setIsLockedOut(true);
      }
    }
  }, []);

  const loadTotalCourseTime = useCallback((totalCourseTimeInSeconds: number): void => {
    if (typeof totalCourseTimeInSeconds === 'number' && totalCourseTimeInSeconds >= 0) {
      const offsetKey = 'fastrack_dev_time_offset';
      let devOffset = 0;
      try {
        devOffset = parseInt(localStorage.getItem(offsetKey) || '0', 10);
        if (isNaN(devOffset)) devOffset = 0;
      } catch(e) {}
      
      const newValue = totalCourseTimeInSeconds + devOffset;
      setTotalCourseTime(newValue);
      totalCourseTimeRef.current = newValue;
    }
  }, []);

  const sessionTimer = useMemo(() => ({
    sessionTime,
    totalTime,
    totalCourseTime,
    isActive,
    isPaused,
    isLockedOut,
    breakRequired,
    sessionMinutes: Math.floor(sessionTime / 60),
    sessionSeconds: sessionTime % 60,
    totalMinutes: Math.floor(totalTime / 60),
    totalSeconds: totalTime % 60,
    totalCourseMinutes: Math.floor(totalCourseTime / 60),
    totalCourseSeconds: totalCourseTime % 60,
    startTimer: startTimerImpl,
    stopTimer: stopTimerImpl,
    pauseTimer: pauseTimerImpl,
    resumeTimer: resumeTimerImpl,
    checkDailyLockout: async (checkFunction: () => Promise<boolean>): Promise<boolean | undefined> => {
      if (checkFunction && typeof checkFunction === 'function') {
        try {
          const isLocked = await checkFunction();
          setIsLockedOut(isLocked);
          return isLocked;
        } catch (error) {
          console.error('Error checking daily lockout:', error);
          return false;
        }
      }
    },
    loadDailyTime,
    loadTotalCourseTime,
    resetBreakRequired: () => {
      setBreakRequired(false);
      breakCheckRef.current = false;
    },
    resetSessionTime: () => {
      lastBreakTimeRef.current = sessionTimeRef.current;
      breakCheckRef.current = false;
      setBreakRequired(false);
    },
    fastForward: (seconds: number) => {
      setSessionTime(prev => {
        const newTime = prev + seconds;
        sessionTimeRef.current = newTime;
        if (SESSION_TIME_KEY) {
          localStorage.setItem(SESSION_TIME_KEY, newTime.toString());
        }
        return newTime;
      });
      setTotalTime(prev => {
        const newVal = prev + seconds;
        totalTimeRef.current = newVal;
        return newVal;
      });
      setTotalCourseTime(prev => {
        const newVal = prev + seconds;
        totalCourseTimeRef.current = newVal;
        return newVal;
      });
      
      const offsetKey = 'fastrack_dev_time_offset';
      const currentOffset = parseInt(localStorage.getItem(offsetKey) || '0', 10);
      localStorage.setItem(offsetKey, (currentOffset + seconds).toString());
    }
  }), [sessionTime, totalTime, totalCourseTime, isActive, isPaused, isLockedOut, breakRequired, SESSION_TIME_KEY, startTimerImpl, stopTimerImpl, pauseTimerImpl, resumeTimerImpl, loadDailyTime, loadTotalCourseTime]);

  // Load session time from storage if it exists
  useEffect(() => {
    if (SESSION_TIME_KEY) {
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('fastrack_session_time_') && key !== SESSION_TIME_KEY) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      } catch (error) {
        console.error('Failed to clean up orphaned session time entries:', error);
      }

      try {
        const saved = localStorage.getItem(SESSION_TIME_KEY);
        if (saved) {
          const parsed = parseInt(saved, 10);
          if (!isNaN(parsed) && parsed > 0) {
            setSessionTime(parsed);
          }
        }
      } catch (error) {
        console.error('Failed to load session time from storage:', error);
      }
    }
    setHydrated(true);
  }, [SESSION_TIME_KEY]);

  // Persist session time to localStorage whenever it changes
  useEffect(() => {
    if (hydrated && sessionTime > 0 && SESSION_TIME_KEY) {
      try {
        localStorage.setItem(SESSION_TIME_KEY, sessionTime.toString());
      } catch (error) {
        console.error('Failed to persist session time:', error);
      }
    }
  }, [sessionTime, hydrated, SESSION_TIME_KEY]);

  const breakManager = useComplianceBreakManager({
    userId: user?.uid,
    sessionId: sessionData.currentSessionId,
    sessionTime: sessionTimer.sessionTime,
    sessionTimeRef: sessionTimeRef,
    sessionTimer,
    lastBreakTimeRef
  });

  const { breakTime } = breakManager;

  // Sync state to high-frequency refs for domain managers
  useEffect(() => {
    videoProgressRef.current = videoProgress;
  }, [videoProgress]);

  useEffect(() => {
    breakHistoryRef.current = breakManager.breakHistory;
  }, [breakManager.breakHistory]);

  const setupPageUnloadHandler = useCallback((_sessionId: string) => {
    // Left as stub for backwards compatibility, handled inside useSessionUnloadBeacon
  }, []);

  const removePageUnloadHandler = useCallback(() => {
    // Left as stub for backwards compatibility, handled inside useSessionUnloadBeacon
  }, []);

  // Domain Managers (Refactored from inline logic)
  const sessionManager = useComplianceSessionManager({
    userId: user?.uid,
    courseId,
    lessonId: currentLessonId,
    ipAddress,
    sessionData,
    isActive,
    isLockedOut,
    startTimer: async () => startTimerImpl(),
    pauseTimer: async () => pauseTimerImpl(),
    stopTimer: async () => stopTimerImpl(),
    sessionTimeRef: timerRefs.sessionTime,
    lessonTimeRef: timerRefs.lessonTime,
    activityTracking,
    lastSyncedTimeRef,
    videoProgressRef: timerRefs.videoProgress,
    breakHistoryRef: timerRefs.breakHistory,
    setupPageUnloadHandler,
    removePageUnloadHandler
  });

  // Enforce Mandatory Break Trigger inside context clock master
  useEffect(() => {
    if (breakRequired && isActive && !breakManager.isOnBreak) {
      console.warn('[TimerContext] 2-hour compliance threshold reached. Auto-triggering startBreak.');
      breakManager.startBreak();
    }
  }, [breakRequired, isActive, breakManager.isOnBreak, breakManager]);

  // Final Connection: Map the compliance manager's wrapped methods to the exposed context refs
  useEffect(() => {
    startTimerRef.current = sessionManager.startSession;
    stopTimerRef.current = sessionManager.stopSession;
    pauseTimerRef.current = pauseTimerImpl;
    resumeTimerRef.current = resumeTimerImpl;
  }, [sessionManager.startSession, sessionManager.stopSession, pauseTimerImpl, resumeTimerImpl]);

  // Create refs for simulation data to avoid closure staleness
  const simulationRefs = useRef({
    isOnBreak: false,
    breakStartTime: null as number | null,
    sessionTimer: sessionTimer,
    breakManager: breakManager,
    sessionData: sessionData,
    startBreak: breakManager.startBreak,
    endBreak: breakManager.endBreak,
    setBreakTime: breakManager.setBreakTime,
    setLessonTime: setLessonTime,
    activityTracking: activityTracking,
    saveSessionProgress: undefined as (() => Promise<void>) | undefined
  });

  useEffect(() => {
    simulationRefs.current = {
      isOnBreak: breakManager.isOnBreak,
      breakStartTime: breakManager.breakStartTime,
      sessionTimer,
      breakManager,
      sessionData,
      startBreak: breakManager.startBreak,
      endBreak: breakManager.endBreak,
      setBreakTime: breakManager.setBreakTime,
      setLessonTime: setLessonTime,
      activityTracking,
      saveSessionProgress: sessionManager.saveSessionProgress
    };
  }, [breakManager.isOnBreak, breakManager.breakStartTime, sessionTimer, breakManager, sessionData, breakManager.startBreak, breakManager.endBreak, breakManager.setBreakTime, setLessonTime, activityTracking, sessionManager.saveSessionProgress]);

  // Hook 1: Asynchronous Compliance Hydration on Initial Session Load
  useComplianceHydration({
    userId: user?.uid,
    courseId,
    lessonId,
    sessionTimer,
    breakManager,
    sessionData,
    breakComplianceManager: breakManager,
    setLessonTime,
    lessonTimeRef,
    lastBreakTimeRef,
    progressDataRef,
    setIsInitialized,
    setSessionTime,
    sessionTimeRef,
    lastSyncedTimeRef
  });

  // Hook 2: Master Ticker and progress auto-saves
  useMasterClock({
    isActive,
    isPaused,
    isOnBreak: breakManager.isOnBreak,
    setSessionTime,
    setTotalTime,
    setTotalCourseTime,
    setLessonTime,
    setIsLockedOut,
    setIsActive,
    setIsPaused,
    setBreakRequired,
    sessionTimeRef,
    totalTimeRef,
    totalCourseTimeRef,
    lessonTimeRef,
    lastBreakTimeRef,
    breakCheckRef,
    lastTickRef,
    intervalRef,
    saveIntervalRef,
    saveSessionProgress: sessionManager.saveSessionProgress,
    videoProgress,
    lessonsAccessed: sessionData.lessonsAccessed,
    breakHistory: breakManager.breakHistory
  });

  // Hook 3: Page Unload event listener and component unmount beacons
  useSessionUnloadBeacon({
    userId: user?.uid,
    courseId,
    sessionId: sessionData.currentSessionId,
    isActive,
    isOnBreak: breakManager.isOnBreak,
    sessionTimeRef,
    lessonTimeRef,
    currentLessonIdRef,
    lastSyncedTimeRef,
    beforeUnloadHandlerRef
  });

  // Hook 4: DevTools simulation engine window.fastrackDev
  useTimerSimulator({
    courseId,
    userId: user?.uid,
    sessionData,
    simulationRefs,
    lessonTimeRef,
    lastBreakTimeRef,
    breakCheckRef,
    setBreakRequired
  });

  const resetSessionTimer = useCallback(() => {
    sessionTimer.resetSessionTime();
  }, [sessionTimer]);

  const resetLessonTime = useCallback(() => {
    setLessonTime(0);
    lessonTimeRef.current = 0;
  }, []);

  const updateVideoProgress = useCallback((currentTime: number, duration: number, percentWatched: number) => {
    setVideoProgress({
      currentTime,
      duration,
      percentWatched
    });
  }, []);

  const trackLessonAccess = useCallback(async (newLessonId: string) => {
    if (currentLessonIdRef.current === newLessonId) {
      return;
    }

    const { recordLessonAccess } = sessionData;
    recordLessonAccess(newLessonId);

    const prevLessonId = currentLessonIdRef.current;
    const prevLessonTime = lessonTimeRef.current;

    // Save fractional progress of the previous lesson if transitioning to a different one
    if (prevLessonId && prevLessonId !== newLessonId && prevLessonTime > 0 && user?.uid && courseId) {
      const uId = user.uid;
      const cId = courseId;
      updateLessonProgress(uId, cId, prevLessonId, { timeSpent: prevLessonTime }).catch(err => {
        console.error(`[TimerContext] Failed to save progress for previous lesson ${prevLessonId}:`, err);
      });
    }

    currentLessonIdRef.current = newLessonId;
    setCurrentLessonId(newLessonId);
    setLessonTime(0);
    lessonTimeRef.current = 0;

    if (user?.uid && courseId) {
      try {
        const progress = await getProgress(user.uid, courseId, true);
        if (progress && currentLessonIdRef.current === newLessonId) {
          progressDataRef.current = progress;
          const persistentCredit = progress.lessonProgress?.[newLessonId]?.timeSpent || 0;
          setLessonTime(persistentCredit);
          lessonTimeRef.current = persistentCredit;
        }
      } catch (err) {
        console.error('[TimerContext] Failed to load lesson credit:', err);
      }
    }
  }, [user?.uid, courseId, sessionData]);

  const isBreakRecommended = useCallback(() => {
    return sessionTime >= (appConfig.RECOMMENDED_BREAK_AFTER || 5400) && !breakManager.isOnBreak;
  }, [sessionTime, breakManager.isOnBreak]);

  const isMaxDailyHoursReached = useCallback(() => {
    return totalTime >= (appConfig.MAX_DAILY_HOURS * 3600 || 14400);
  }, [totalTime]);

  const formatTime = useCallback((seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const getFormattedSessionTime = useCallback(() => formatTime(sessionTime), [formatTime, sessionTime]);
  const getFormattedTotalTime = useCallback(() => formatTime(totalTime), [formatTime, totalTime]);
  const getFormattedBreakTime = useCallback(() => formatTime(breakTime), [formatTime, breakTime]);

  const getRemainingDailyTime = useCallback(() => {
    const maxSeconds = (appConfig.MAX_DAILY_HOURS || 4) * 3600;
    return Math.max(0, maxSeconds - totalTime);
  }, [totalTime]);

  const getActualBreakDuration = useCallback(() => breakManager.currentBreakDuration, [breakManager.currentBreakDuration]);
  const hasBreakMetMinimumDuration = useCallback(() => breakManager.isBreakMinimumMet, [breakManager.isBreakMinimumMet]);

  const timeUntilBreak = Math.max(0, BREAK_REQUIRED_AFTER - (sessionTime - lastBreakTimeRef.current));

  const value: TimerContextValue = React.useMemo(() => ({
    sessionTime,
    totalTime,
    totalCourseTime,
    isActive,
    isPaused,
    breakTime,
    isOnBreak: breakManager.isOnBreak,
    isLockedOut,
    isBreakMandatory: breakManager.isBreakMandatory,
    currentSessionId: sessionData.currentSessionId,
    videoProgress,
    lessonsAccessed: sessionData.lessonsAccessed,
    sessionHistory: sessionData.sessionHistory,
    breakHistory: breakManager.breakHistory,
    error,
    setError,
    startTimer: startTimerComplianceWrapped,
    pauseTimer: pauseTimerComplianceWrapped,
    resumeTimer: resumeTimerComplianceWrapped,
    stopTimer: stopTimerComplianceWrapped,
    resetSessionTimer,
    startBreak: breakManager.startBreak,
    endBreak: breakManager.endBreak,
    triggerPVQ: async () => { console.warn('triggerPVQ decoupled'); },
    updateVideoProgress,
    trackLessonAccess,
    isBreakRecommended,
    isMaxDailyHoursReached,
    getFormattedSessionTime,
    getFormattedTotalTime,
    getFormattedBreakTime,
    getRemainingDailyTime,
    formatTime,
    getActualBreakDuration,
    hasBreakMetMinimumDuration,
    lessonTime,
    resetLessonTime,
    sessionTimer,
    timerRefs,
    checkDailyLockout: sessionTimer.checkDailyLockout,
    timeUntilBreak,
    isInitialized,
    activityTracking
  }), [
    sessionTime,
    totalTime,
    totalCourseTime,
    isActive,
    isPaused,
    isLockedOut,
    breakTime,
    breakManager.isOnBreak,
    breakManager.isBreakMandatory,
    breakManager.breakHistory,
    breakManager.currentBreakDuration,
    breakManager.isBreakMinimumMet,
    sessionData.currentSessionId,
    sessionData.lessonsAccessed,
    sessionData.sessionHistory,
    videoProgress,
    error,
    setError,
    startTimerComplianceWrapped,
    pauseTimerComplianceWrapped,
    resumeTimerComplianceWrapped,
    stopTimerComplianceWrapped,
    resetSessionTimer,
    breakManager.startBreak,
    breakManager.endBreak,
    updateVideoProgress,
    trackLessonAccess,
    isBreakRecommended,
    isMaxDailyHoursReached,
    getFormattedSessionTime,
    getFormattedTotalTime,
    getFormattedBreakTime,
    getRemainingDailyTime,
    formatTime,
    getActualBreakDuration,
    hasBreakMetMinimumDuration,
    lessonTime,
    resetLessonTime,
    sessionTimer,
    timerRefs,
    timeUntilBreak,
    isInitialized,
    activityTracking
  ]);

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
};

export default TimerProvider;
